/**
 * Staff-only Pulse operator surface at /staff/pulse/.
 *
 * Cross-tenant overview: counts, top tenants, recent failures. The
 * row that on-call actually opens this page for is the recent
 * failures table - clicking through goes to the diagnose tool.
 *
 * Backed by GET /api/staff/pulse/overview/.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { AlertTriangle, Inbox, Search } from 'lucide-react'

import api from '@/admin/lib/api'
import { useFormatting } from '@/admin/hooks/use-formatting'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { StaffShell } from '@/admin/components/staff/staff-shell'

interface FailureRow {
  delivery_uuid: string
  tenant_id: number
  event_uuid: string
  event_key: string
  status: string
  consecutive_failures: number
  target_url: string | null
  last_attempt_at: string | null
  created_at: string
}

interface TopTenantRow {
  tenant_id: number
  slug: string
  name: string
  events: number
}

interface HourlyBucket {
  hour: string
  events: number
  delivered: number
  failed: number
  pending: number
}

interface OverviewPayload {
  window_24h: {
    events: number
    deliveries: number
    delivered: number
    failed: number
    pending: number
    success_rate: number | null
  }
  window_7d: { events: number }
  series_24h: HourlyBucket[]
  top_tenants_7d: TopTenantRow[]
  recent_failures: FailureRow[]
}

export function StaffPulsePage() {
  const navigate = useNavigate()
  const { formatNumber, locale } = useFormatting()
  const overview = useQuery<OverviewPayload>({
    queryKey: ['staff-pulse-overview'],
    queryFn: () =>
      api
        .get('/staff/pulse/overview/')
        .then((r) => (r.data?.result ?? r.data) as OverviewPayload),
    refetchInterval: 30_000,
  })

  const d = overview.data

  return (
    <StaffShell>
    <div className="mx-auto mt-8 max-w-[1100px] px-6 pb-12">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-[28px] leading-tight text-[var(--ink)]">
            Pulse
          </h1>
          <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">
            Cross-tenant event delivery operator surface. Read-only view of
            volume, success, and recent failures.
          </p>
        </div>
        <Button size="sm" onClick={() => navigate('/staff/pulse/diagnose')}>
          <Search className="mr-1.5 h-3 w-3" />
          Diagnose event
        </Button>
      </div>

      {/* Summary cards */}
      <div className="mb-6 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
        <Metric
          label="Events (24h)"
          value={d ? formatNumber(d.window_24h.events) : '-'}
          sub={d ? `${formatNumber(d.window_7d.events)} over 7d` : undefined}
        />
        <Metric
          label="Deliveries (24h)"
          value={d ? formatNumber(d.window_24h.deliveries) : '-'}
          sub={
            d
              ? `${formatNumber(d.window_24h.delivered)} ok · ${formatNumber(d.window_24h.failed)} failed · ${formatNumber(d.window_24h.pending)} pending`
              : undefined
          }
        />
        <Metric
          label="Success rate (24h)"
          value={
            d?.window_24h.success_rate != null
              ? `${d.window_24h.success_rate}%`
              : '-'
          }
          sub={
            d?.window_24h.success_rate != null
              ? 'delivered / (delivered + failed)'
              : 'no terminal deliveries'
          }
        />
        <Metric
          label="Active tenants (7d)"
          value={d ? formatNumber(d.top_tenants_7d.length) : '-'}
          sub={d ? 'firing at least one event' : undefined}
        />
      </div>

      {/* Hourly delivery health */}
      <Section title="Delivery health by hour (last 24h)">
        {d ? (
          <HourlySeriesChart data={d.series_24h} />
        ) : (
          <div className="h-[180px]" />
        )}
      </Section>

      {/* Top tenants */}
      <Section title="Top tenants by event volume (7d)">
        <DataTable<TopTenantRow>
          tableId="staff-pulse-top-tenants"
          columns={topTenantColumns(formatNumber)}
          data={d?.top_tenants_7d ?? []}
          rowKey={(r) => String(r.tenant_id)}
          emptyStateNode={
            <Empty icon={Inbox} message="No tenants fired events in the last 7 days." />
          }
        />
      </Section>

      {/* Recent failures */}
      <Section title={`Recent failures (${d?.recent_failures.length ?? 0})`}>
        <DataTable<FailureRow>
          tableId="staff-pulse-recent-failures"
          columns={failureColumns(navigate, locale)}
          data={d?.recent_failures ?? []}
          rowKey={(r) => r.delivery_uuid}
          emptyStateNode={
            <Empty
              icon={AlertTriangle}
              message="No recent failures - the rotation is quiet."
            />
          }
        />
      </Section>
    </div>
    </StaffShell>
  )
}

// ---------------------------------------------------------------------------
// chrome
// ---------------------------------------------------------------------------

function Metric({
  label,
  value,
  sub,
}: {
  label: string
  value: string
  sub?: string
}) {
  return (
    <div className="rounded-[8px] bg-[var(--bg-subtle)] px-[18px] py-4">
      <p className="mb-2 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {label}
      </p>
      <p className="font-display text-[28px] leading-none text-[var(--ink)]">
        {value}
      </p>
      {sub && <p className="mt-1.5 text-[11px] text-[var(--ink-3)]">{sub}</p>}
    </div>
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
        {title}
      </h2>
      <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)]">
        {children}
      </div>
    </section>
  )
}

function Empty({
  icon: Icon,
  message,
}: {
  icon: typeof AlertTriangle
  message: string
}) {
  return (
    <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
      <Icon className="h-4 w-4 text-[var(--ink-3)]" />
      <p className="text-[12px] text-[var(--ink-3)]">{message}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Column factories
// ---------------------------------------------------------------------------

function topTenantColumns(
  formatNumber: (n: number) => string,
): DataTableColumn<TopTenantRow>[] {
  return [
    { id: 'name', header: 'Tenant', accessorKey: 'name' },
    {
      id: 'slug',
      header: 'Slug',
      cell: (r) => (
        <span className="font-mono text-[11.5px] text-[var(--ink-3)]">{r.slug}</span>
      ),
    },
    {
      id: 'events',
      header: 'Events',
      cell: (r) => (
        <span className="block text-right font-mono text-[var(--ink)]">
          {formatNumber(r.events)}
        </span>
      ),
    },
  ]
}

function failureColumns(
  navigate: ReturnType<typeof useNavigate>,
  locale: string,
): DataTableColumn<FailureRow>[] {
  return [
    {
      id: 'created_at',
      header: 'When',
      cell: (r) => (
        <span className="font-mono text-[11px] text-[var(--ink-3)]">
          {new Date(r.created_at).toLocaleString(locale, {
            month: 'short',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
      ),
    },
    {
      id: 'event_key',
      header: 'Event',
      cell: (r) => (
        <span className="font-mono text-[11.5px] text-[var(--ink-2)]">
          {r.event_key}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => <Badge variant="destructive">{r.status}</Badge>,
    },
    {
      id: 'target_url',
      header: 'Endpoint',
      cell: (r) => (
        <span className="block truncate font-mono text-[11px] text-[var(--ink-3)]">
          {r.target_url ?? '-'}
        </span>
      ),
    },
    {
      id: 'streak',
      header: 'Streak',
      cell: (r) => (
        <span className="font-mono text-[11px] text-[var(--ink)]">
          {r.consecutive_failures}
        </span>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      cell: (r) => (
        <div className="text-right">
          <Button
            variant="ghost"
            size="xs"
            onClick={() =>
              navigate(`/staff/pulse/diagnose?event_uuid=${r.event_uuid}`)
            }
          >
            Diagnose
          </Button>
        </div>
      ),
    },
  ]
}

/**
 * 24-bar stacked SVG of delivery health, one bar per hour.
 *
 * Inline rather than recharts: one chart, internal page, no upside to
 * pulling a 150KB dep onto every staff bundle.
 */
function HourlySeriesChart({ data }: { data: HourlyBucket[] }) {
  const [hover, setHover] = useState<number | null>(null)
  const { locale } = useFormatting()
  const max = Math.max(
    1,
    ...data.map((b) => b.delivered + b.failed + b.pending),
  )
  const barW = 18
  const gap = 6
  const padX = 16
  const padTop = 16
  const padBottom = 28
  const chartH = 140
  const W = padX * 2 + data.length * (barW + gap) - gap
  const H = chartH + padTop + padBottom
  const scale = (n: number) => (n / max) * chartH

  return (
    <div className="relative px-4 py-4">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="block w-full"
        preserveAspectRatio="none"
      >
        {/* faint baseline */}
        <line
          x1={padX}
          x2={W - padX}
          y1={padTop + chartH}
          y2={padTop + chartH}
          stroke="var(--rule)"
          strokeWidth={0.5}
        />
        {data.map((b, i) => {
          const x = padX + i * (barW + gap)
          const baseY = padTop + chartH
          const dh = scale(b.delivered)
          const fh = scale(b.failed)
          const ph = scale(b.pending)
          return (
            <g
              key={b.hour}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
              className="cursor-default"
            >
              {/* invisible hit area for hover */}
              <rect
                x={x - gap / 2}
                y={padTop}
                width={barW + gap}
                height={chartH + padBottom}
                fill="transparent"
              />
              <rect
                x={x}
                y={baseY - dh}
                width={barW}
                height={dh}
                fill="var(--success-fg, #16a34a)"
                opacity={hover === null || hover === i ? 1 : 0.4}
              />
              <rect
                x={x}
                y={baseY - dh - fh}
                width={barW}
                height={fh}
                fill="var(--danger-fg, #dc2626)"
                opacity={hover === null || hover === i ? 1 : 0.4}
              />
              <rect
                x={x}
                y={baseY - dh - fh - ph}
                width={barW}
                height={ph}
                fill="var(--warning-fg, #d97706)"
                opacity={hover === null || hover === i ? 1 : 0.4}
              />
            </g>
          )
        })}
        {/* x-axis labels: oldest + middle + now */}
        {[0, Math.floor(data.length / 2), data.length - 1].map((idx) => {
          const x = padX + idx * (barW + gap) + barW / 2
          const d = data[idx]
          if (!d) return null
          const label = new Date(d.hour).toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit',
          })
          return (
            <text
              key={idx}
              x={x}
              y={H - 10}
              textAnchor="middle"
              className="fill-[var(--ink-3)] text-[10px]"
            >
              {label}
            </text>
          )
        })}
        {/* y-axis max label */}
        <text
          x={padX}
          y={padTop - 4}
          className="fill-[var(--ink-3)] text-[10px]"
        >
          max {max}
        </text>
      </svg>

      {/* tooltip + legend */}
      <div className="mt-2 flex items-center justify-between text-[11px]">
        <div className="flex items-center gap-3 text-[var(--ink-3)]">
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-sm bg-[var(--success-fg,#16a34a)]" />
            Delivered
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-sm bg-[var(--danger-fg,#dc2626)]" />
            Failed
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block h-2 w-2 rounded-sm bg-[var(--warning-fg,#d97706)]" />
            Pending
          </span>
        </div>
        <div className="font-mono text-[11px] text-[var(--ink-2)]">
          {hover !== null && data[hover]
            ? `${new Date(data[hover].hour).toLocaleString(locale, { month: 'short', day: '2-digit', hour: '2-digit' })} · ${data[hover].events} events · ${data[hover].delivered} ok · ${data[hover].failed} failed · ${data[hover].pending} pending`
            : 'hover a bar for details'}
        </div>
      </div>
    </div>
  )
}
